import { makeAutoObservable } from 'mobx'

// Листові модулі механізму Ship `OfficeViewer`, а не його index: index тягне
// стор шухляди друку з `@/Core`, а стор переглядача створюється під час імпорту
import {
    COLLABORA,
    isFromEditor,
    originOf,
    parseCollaboraMessage,
    postToEditor,
} from '@/Ship/Components/OfficeViewer/collaboraMessages'
import { closeWelcomeDialog, injectHiddenElementsStyle } from '@/Ship/Components/OfficeViewer/hiddenElements'

import type { EditSession } from '@/Ship/Components/OfficeViewer'

/**
 * Скільки чекати `Document_Loaded`, перш ніж сказати, що редактор не
 * відповідає: великий xlsx у POC відкривався до 5 с, холодний старт
 * Collabora — довше.
 */
export const DISK_OFFICE_LOAD_TIMEOUT_MS = 45_000

/**
 * Документ Office у переглядачі диска — Collabora в режимі перегляду
 * (`DiskOfficeView`): сесія `edit_session`, вікно `iframe` і повідомлення
 * редактора. Механізм той самий, що в шухляді друку (`OfficeViewerStore`
 * Ship): приймаємо лише повідомлення від нашого `iframe` і з origin
 * редактора, на `Document_Loaded` — `Host_PostmessageReady`, приховані
 * елементи інтерфейсу й закрите вікно привітання.
 */
export class DiskOfficeStore {
    session: EditSession | null = null
    /** Документ показано (`App_LoadingStatus: Document_Loaded`). */
    loaded = false
    /** Редактор не відповів за {@link DISK_OFFICE_LOAD_TIMEOUT_MS}. */
    failed = false

    private frame: HTMLIFrameElement | null = null
    private timer: ReturnType<typeof setTimeout> | null = null

    constructor(private readonly timeoutMs = DISK_OFFICE_LOAD_TIMEOUT_MS) {
        makeAutoObservable<DiskOfficeStore, 'frame' | 'timer' | 'timeoutMs'>(this, {
            frame: false,
            timer: false,
            timeoutMs: false,
        })
    }

    get editorOrigin(): string | null {
        return this.session ? originOf(this.session.actionUrl) : null
    }

    open(session: EditSession): void {
        this.reset()
        this.session = session
        this.timer = setTimeout(() => this.timedOut(), this.timeoutMs)
    }

    attachFrame(frame: HTMLIFrameElement | null): void {
        this.frame = frame
    }

    /** Обробник `message` вікна; чуже — ігнорується. */
    handleMessage(event: Pick<MessageEvent, 'source' | 'origin' | 'data'>): void {
        if (!isFromEditor(event, this.frame?.contentWindow, this.editorOrigin)) return

        const message = parseCollaboraMessage(event.data)

        if (message?.MessageId !== COLLABORA.loadingStatus || message.Values?.['Status'] !== COLLABORA.documentLoaded) {
            return
        }

        postToEditor(this.frame?.contentWindow, this.editorOrigin, { MessageId: COLLABORA.hostReady })
        injectHiddenElementsStyle(this.frame)
        closeWelcomeDialog(this.frame)
        this.loaded = true
        this.failed = false
        this.clearTimer()
    }

    reset(): void {
        this.clearTimer()
        this.session = null
        this.loaded = false
        this.failed = false
        this.frame = null
    }

    private timedOut(): void {
        this.timer = null
        if (!this.loaded) this.failed = true
    }

    private clearTimer(): void {
        if (this.timer !== null) clearTimeout(this.timer)
        this.timer = null
    }
}
